<div class="router-templates-container">
  <!-- Templates List Panel -->
  <div class="templates-list-panel">
    <div class="panel-header">
      <h3>Router Templates</h3>
      <button kendoButton themeColor="primary" (click)="createNewTemplate()">
        <span class="k-icon k-font-icon k-i-plus"></span> New Template
      </button>
    </div>

    <kendo-grid
      [data]="templates"
      [loading]="loading"
      [selectable]="{ mode: 'single' }"
      (selectionChange)="onTemplateSelect($event)"
      class="templates-grid"
      [rowClass]="rowCallback"
    >
      <kendo-grid-column field="name" title="Name" [width]="180"></kendo-grid-column>
      <kendo-grid-column field="productLine" title="Product Line" [width]="100"></kendo-grid-column>
      <kendo-grid-column field="isActive" title="Active" [width]="70">
        <ng-template kendoGridCellTemplate let-dataItem>
          <span [class.active-badge]="dataItem.isActive" [class.inactive-badge]="!dataItem.isActive">
            {{ dataItem.isActive ? 'Yes' : 'No' }}
          </span>
        </ng-template>
      </kendo-grid-column>
      <kendo-grid-column field="isDefault" title="Default" [width]="70">
        <ng-template kendoGridCellTemplate let-dataItem>
          <span class="k-icon k-font-icon k-i-check" *ngIf="dataItem.isDefault" style="color: var(--kendo-color-success);"></span>
        </ng-template>
      </kendo-grid-column>
    </kendo-grid>
  </div>

  <!-- Template Editor Panel -->
  <div class="template-editor-panel" *ngIf="selectedTemplate || isNewTemplate">
    <!-- Template Header -->
    <div class="panel-header">
      <h3>{{ isNewTemplate ? 'New Template' : selectedTemplate?.name }}</h3>
      <div class="header-actions" *ngIf="!isEditingTemplate">
        <button kendoButton (click)="editTemplate()">
          <span class="k-icon k-font-icon k-i-edit"></span> Edit
        </button>
        <button kendoButton (click)="duplicateTemplate()">
          <span class="k-icon k-font-icon k-i-copy"></span> Duplicate
        </button>
        <button kendoButton themeColor="error" (click)="deleteTemplate()">
          <span class="k-icon k-font-icon k-i-delete"></span> Delete
        </button>
      </div>
    </div>

    <!-- Template Form (Edit Mode) -->
    <form *ngIf="isEditingTemplate" [formGroup]="templateForm" class="template-form">
      <div class="form-grid">
        <div class="form-row">
          <label>Name *</label>
          <kendo-textbox formControlName="name" placeholder="Template name"></kendo-textbox>
        </div>

        <div class="form-row">
          <label>Description</label>
          <kendo-textarea formControlName="description" placeholder="Template description" [rows]="2"></kendo-textarea>
        </div>

        <div class="form-row-group">
          <div class="form-row">
            <label>Product Line</label>
            <kendo-dropdownlist
              formControlName="productLine"
              [data]="productLines"
              [valuePrimitive]="true"
              (valueChange)="onProductLineChange()"
            ></kendo-dropdownlist>
          </div>

          <div class="form-row">
            <label>Sub-PSL</label>
            <kendo-dropdownlist
              formControlName="subPSL"
              [data]="subPSLs"
              [valuePrimitive]="true"
            ></kendo-dropdownlist>
          </div>
        </div>

        <div class="form-row-group">
          <div class="form-row">
            <label>Bit Series</label>
            <kendo-dropdownlist
              formControlName="bitSeries"
              [data]="bitSeriesList"
              [valuePrimitive]="true"
            ></kendo-dropdownlist>
          </div>

          <div class="form-row">
            <label>Plant</label>
            <kendo-dropdownlist
              formControlName="plant"
              [data]="plants"
              [valuePrimitive]="true"
            ></kendo-dropdownlist>
          </div>
        </div>

        <div class="form-row-group">
          <div class="form-row checkbox-row">
            <kendo-switch formControlName="isActive"></kendo-switch>
            <label>Active</label>
          </div>

          <div class="form-row checkbox-row">
            <kendo-switch formControlName="isDefault"></kendo-switch>
            <label>Default Template</label>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button kendoButton (click)="cancelTemplateEdit()" type="button">Cancel</button>
        <button kendoButton themeColor="primary" (click)="saveTemplate()" [disabled]="templateForm.invalid">
          {{ isNewTemplate ? 'Create' : 'Save' }}
        </button>
      </div>
    </form>

    <!-- Template View (Read-only) -->
    <div *ngIf="!isEditingTemplate && selectedTemplate" class="template-view">
      <div class="info-grid">
        <div class="info-item" *ngIf="selectedTemplate.description">
          <label>Description</label>
          <span>{{ selectedTemplate.description }}</span>
        </div>
        <div class="info-item">
          <label>Product Line</label>
          <span>{{ selectedTemplate.productLine || '—' }}</span>
        </div>
        <div class="info-item">
          <label>Sub-PSL</label>
          <span>{{ selectedTemplate.subPSL || '—' }}</span>
        </div>
        <div class="info-item">
          <label>Bit Series</label>
          <span>{{ selectedTemplate.bitSeries || '—' }}</span>
        </div>
        <div class="info-item">
          <label>Plant</label>
          <span>{{ selectedTemplate.plant || '—' }}</span>
        </div>
        <div class="info-item">
          <label>Version</label>
          <span>{{ selectedTemplate.version }}</span>
        </div>
      </div>
    </div>

    <!-- Steps Section -->
    <div class="steps-section" *ngIf="selectedTemplate && !isEditingTemplate">
      <div class="section-header">
        <h4>Router Steps ({{ steps.length }})</h4>
        <button kendoButton size="small" themeColor="primary" (click)="createNewStep()">
          <span class="k-icon k-font-icon k-i-plus"></span> Add Step
        </button>
      </div>

      <!-- Steps List -->
      <div class="steps-list" *ngIf="!stepsLoading && steps.length > 0">
        <div
          *ngFor="let step of steps; let i = index"
          class="step-card"
          [class.selected]="selectedStep?.id === step.id"
          [class.inactive]="!step.isActive"
        >
          <div class="step-sequence">{{ step.sequence }}</div>
          <div class="step-content">
            <div class="step-header">
              <span class="step-description">{{ step.description }}</span>
              <span class="step-work-center">{{ step.workCenter }}</span>
            </div>
            <div class="step-details">
              <span *ngIf="step.category" class="step-category">{{ step.category }}</span>
              <span *ngIf="step.standardTime" class="step-time">{{ step.standardTime }} {{ step.timeUnit }}</span>
              <span *ngIf="!step.isRequired" class="step-optional">Optional</span>
              <span *ngIf="step.isParallel" class="step-parallel">Parallel</span>
            </div>
          </div>
          <div class="step-actions">
            <button kendoButton size="small" fillMode="flat" (click)="moveStepUp(step)" [disabled]="i === 0" title="Move Up">
              <span class="k-icon k-font-icon k-i-arrow-up"></span>
            </button>
            <button kendoButton size="small" fillMode="flat" (click)="moveStepDown(step)" [disabled]="i === steps.length - 1" title="Move Down">
              <span class="k-icon k-font-icon k-i-arrow-down"></span>
            </button>
            <button kendoButton size="small" fillMode="flat" (click)="editStep(step)" title="Edit">
              <span class="k-icon k-font-icon k-i-edit"></span>
            </button>
            <button kendoButton size="small" fillMode="flat" (click)="deleteStep(step)" title="Delete">
              <span class="k-icon k-font-icon k-i-delete"></span>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div class="empty-steps" *ngIf="!stepsLoading && steps.length === 0">
        <span class="k-icon k-font-icon k-i-list-unordered"></span>
        <p>No steps defined yet. Click "Add Step" to create the first step.</p>
      </div>

      <!-- Loading -->
      <div class="loading-steps" *ngIf="stepsLoading">
        <kendo-loader size="medium"></kendo-loader>
        <span>Loading steps...</span>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div class="template-editor-panel empty-state" *ngIf="!selectedTemplate && !isNewTemplate">
    <div class="empty-message">
      <span class="k-icon k-font-icon k-i-track-changes-enable k-icon-xl"></span>
      <p>Select a template from the list or create a new one</p>
    </div>
  </div>
</div>

<!-- Step Editor Dialog -->
<kendo-dialog
  *ngIf="isEditingStep"
  [title]="isNewStep ? 'Add Router Step' : 'Edit Router Step'"
  (close)="cancelStepEdit()"
  [width]="600"
>
  <form [formGroup]="stepForm" class="step-form">
    <div class="dialog-form-grid">
      <div class="form-row-group">
        <div class="form-row">
          <label>Sequence *</label>
          <kendo-numerictextbox
            formControlName="sequence"
            [min]="1"
            [step]="10"
            [format]="'n0'"
          ></kendo-numerictextbox>
        </div>

        <div class="form-row">
          <label>Operation Code</label>
          <kendo-textbox formControlName="operationCode" placeholder="e.g., 0010"></kendo-textbox>
        </div>
      </div>

      <div class="form-row">
        <label>Description *</label>
        <kendo-textbox formControlName="description" placeholder="Step description"></kendo-textbox>
      </div>

      <div class="form-row-group">
        <div class="form-row">
          <label>Work Center *</label>
          <kendo-dropdownlist
            formControlName="workCenter"
            [data]="workCenters"
            textField="name"
            valueField="code"
            [valuePrimitive]="true"
          ></kendo-dropdownlist>
        </div>

        <div class="form-row">
          <label>Control Key</label>
          <kendo-dropdownlist
            formControlName="controlKey"
            [data]="controlKeys"
            textField="label"
            valueField="value"
            [valuePrimitive]="true"
          ></kendo-dropdownlist>
        </div>
      </div>

      <div class="form-row-group">
        <div class="form-row">
          <label>Category</label>
          <kendo-dropdownlist
            formControlName="category"
            [data]="stepCategories"
            [valuePrimitive]="true"
          ></kendo-dropdownlist>
        </div>

        <div class="form-row">
          <label>Activity Type</label>
          <kendo-textbox formControlName="activityType" placeholder="e.g., 1410"></kendo-textbox>
        </div>
      </div>

      <div class="form-row-group">
        <div class="form-row">
          <label>Standard Time</label>
          <kendo-numerictextbox
            formControlName="standardTime"
            [min]="0"
            [decimals]="2"
          ></kendo-numerictextbox>
        </div>

        <div class="form-row">
          <label>Time Unit</label>
          <kendo-dropdownlist
            formControlName="timeUnit"
            [data]="['MIN', 'HR', 'DAY']"
            [valuePrimitive]="true"
          ></kendo-dropdownlist>
        </div>
      </div>

      <div class="form-row">
        <label>Long Text / Instructions</label>
        <kendo-textarea formControlName="longText" placeholder="Detailed work instructions" [rows]="4"></kendo-textarea>
      </div>

      <div class="form-row">
        <label>Standard Text Key (SAP)</label>
        <kendo-textbox formControlName="standardTextKey" placeholder="Reference to standard SAP text"></kendo-textbox>
      </div>

      <div class="form-row-group checkbox-group">
        <div class="form-row checkbox-row">
          <kendo-switch formControlName="isRequired"></kendo-switch>
          <label>Required Step</label>
        </div>

        <div class="form-row checkbox-row">
          <kendo-switch formControlName="isParallel"></kendo-switch>
          <label>Can Run in Parallel</label>
        </div>

        <div class="form-row checkbox-row">
          <kendo-switch formControlName="isActive"></kendo-switch>
          <label>Active</label>
        </div>
      </div>
    </div>
  </form>

  <kendo-dialog-actions>
    <button kendoButton (click)="cancelStepEdit()">Cancel</button>
    <button kendoButton themeColor="primary" (click)="saveStep()" [disabled]="stepForm.invalid">
      {{ isNewStep ? 'Add Step' : 'Save Changes' }}
    </button>
  </kendo-dialog-actions>
</kendo-dialog>
